import {BoxProps} from '@chakra-ui/react';
import {useHalCollection, useHaligator} from '@synako/haligator';
import {ClientDesc, RootDesc as FFConnectRoot} from 'api-types/ffconnect-hal';
import {useBackofficeClient} from './backoffice/domains/PriseDeCommande/contexts/useBackofficeClient';
import {FollowPromiseOne} from '@synako/halitrek';
import {useParams} from 'react-router-dom';

declare module '@synako/haligator' {
  interface HaliRenderMoreProps {
    style?: BoxProps;
  }
}

// Store pour le client du backoffice (accessible en dehors du contexte React)
let backofficeClientStore: FollowPromiseOne<ClientDesc> | null = null;

// Fonction pour stocker le client
export function setBackofficeClient(
  client: FollowPromiseOne<ClientDesc> | null,
) {
  backofficeClientStore = client;
}

export function useMe() {
  return useApi().follow('get-me');
}

export function useApi() {
  const {client} = useHaligator();
  return client.go<FFConnectRoot>();
}

export function useClient() {
  const {client_id} = useParams<{client_id?: string}>();
  const api = useApi();
  const me = useMe();
  const backofficeClient = useBackofficeClient(true);

  // Routing unifié /ffconnect/c/:client_id (commercial + assistance)
  if (client_id) {
    return api.follow('get-client', {client_id});
  }

  // Utilisation du client du backoffice 'Prise de commande' s'il existe
  if (backofficeClient) {
    return backofficeClient;
  }

  // Si nous n'avons pas de client via le contexte mais qu'il y en a un dans le store
  if (backofficeClientStore) {
    return backofficeClientStore;
  }

  // Sinon, utiliser le client standard
  return me.follow('client');
}

export function useCadencier() {
  return useClient().follow('cadencier');
}

export function useLivraisons() {
  return useClient().follow('livraisons_possibles');
}

export function useNomenclature() {
  return useApi().follow('get-nomenclature');
}

export function useRepresentants() {
  return useClient().follow('representants');
}

export function useSocieteCommerciale() {
  return useClient().follow('societe_commerciale');
}

export function useListes() {
  return useClient().follow('listes');
}

export function useLabelAsset(codeAsset: string) {
  const {client} = useHaligator();
  return client
    .go<FFConnectRoot>()
    .follow('get-label_asset', {code: codeAsset});
}

export function useCommercial() {
  return useClient().follow('commercial');
}

// Hook pour récupérer les devis visibles du client
export function useDevisVisible() {
  const client = useClient();

  // Récupération de tous les devis visibles du client (il ne peut en avoir qu'un)
  const devisCollection = useHalCollection(client.follow('devis'));

  // Récuperation du devis visible (il ne peut en avoir qu'un)
  const devisVisible = devisCollection.items?.find(
    (d) => d.data.statut === 'visible',
  );

  return {
    devis: devisVisible,
    loading: devisCollection.loading,
    error: devisCollection.error,
    refreshing: devisCollection.refreshing,
  };
}
